import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui/slot";
import { cn } from "@/lib/utils";

// shadcn's Button at the pane's sizes. The default is the 28px control MUI's
// `size="small"` was, because that is what nearly every button in a task pane is; `lg`
// is the 32px one for a primary call to action. Variants keep shadcn's names, plus two
// MUI had and the pane uses: `outline` is MUI's "outlined" in the primary colour (an
// accent border and label), `secondary-outline` the grey one, `ghost-primary` MUI's
// "text" in the primary colour.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent text-button transition-colors outline-none select-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-dark",
        destructive: "bg-destructive text-destructive-foreground hover:bg-error-dark",
        outline:
          "border-primary/50 bg-transparent text-primary hover:border-primary hover:bg-primary-tint",
        "secondary-outline": "border-input bg-transparent text-foreground hover:bg-accent",
        secondary: "bg-secondary text-secondary-foreground hover:bg-grey-200",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        "ghost-primary": "text-primary hover:bg-primary-tint",
        link: "h-auto px-0 text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-7 px-2.5 text-[12px]",
        xs: "h-6 gap-1 px-2 text-[11.5px] [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-8 px-3.5 text-[13px]",
        icon: "size-7 [&_svg:not([class*='size-'])]:size-[18px]",
        "icon-xs": "size-6",
        "icon-lg": "size-8 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant ?? "default"}
        // A button inside a form submits unless told otherwise, and almost none of ours
        // should. asChild renders someone else's element, whose type is theirs.
        type={asChild ? type : (type ?? "button")}
        // One cn() over the variant AND the caller's classes, so the caller's colour wins
        // a conflict. Leaving the caller's to Slot concatenates both, and stylesheet order
        // decides -- the web client's white-on-white Delete button.
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
